import { FolderIcon } from '@phosphor-icons/react'
import { TFunction } from 'i18next'

import type { SelectTreeOption } from '@/components/ui/select/select.types'
import { getAssetLink } from '@/helpers/get-asset-link'
import type { CommunityEssentialFolderModel } from '@/models'

import { MAX_FOLDER_DEPTH } from './community-essentials.constants'
import type { EssentialType } from './community-essentials.types'

interface BuildFolderTreeOptionsParams {
  allFolders: CommunityEssentialFolderModel[] | undefined
  checkMaxDepth?: boolean
  editingFolderId?: number
  t: TFunction<['community-essentials']>
}

export const buildFolderTreeOptions = ({
  allFolders,
  checkMaxDepth = false,
  editingFolderId,
  t,
}: BuildFolderTreeOptionsParams): SelectTreeOption<number>[] => {
  const blockedIds = new Set<number>()
  if (editingFolderId !== undefined && allFolders) {
    blockedIds.add(editingFolderId)
    let changed = true
    while (changed) {
      changed = false
      for (const f of allFolders) {
        if (
          f.parentFolderId !== null &&
          blockedIds.has(f.parentFolderId) &&
          !blockedIds.has(f.id)
        ) {
          blockedIds.add(f.id)
          changed = true
        }
      }
    }
  }

  const editingFolder = allFolders?.find((f) => f.id === editingFolderId)
  const subtreeDepth = editingFolder
    ? Math.max(
        0,
        ...Array.from(blockedIds).map(
          (id) =>
            (allFolders!.find((f) => f.id === id)?.nestedLevel ?? 0) - editingFolder.nestedLevel,
        ),
      )
    : 0

  const currentParentId = editingFolder ? editingFolder.parentFolderId : undefined

  const isCurrentParent = (folder: CommunityEssentialFolderModel): boolean =>
    currentParentId !== undefined && currentParentId !== null && folder.id === currentParentId

  const isBlocked = (folder: CommunityEssentialFolderModel): boolean => {
    if (blockedIds.has(folder.id)) return true
    if (checkMaxDepth && folder.nestedLevel + subtreeDepth + 1 >= MAX_FOLDER_DEPTH) return true
    return false
  }

  const buildSuffix = (folder: CommunityEssentialFolderModel): string => {
    if (blockedIds.has(folder.id) && folder.id === editingFolderId)
      return t('community-essentials:folderTree.thisFolderSuffix')
    if (checkMaxDepth && folder.nestedLevel + subtreeDepth + 1 >= MAX_FOLDER_DEPTH)
      return t('community-essentials:folderTree.maxDepthSuffix')
    return ''
  }

  const childrenMap = new Map<number | null, CommunityEssentialFolderModel[]>()
  if (allFolders) {
    for (const folder of allFolders) {
      const parentId = folder.parentFolderId
      if (!childrenMap.has(parentId)) childrenMap.set(parentId, [])
      childrenMap.get(parentId)!.push(folder)
    }
  }

  const buildNodes = (parentId: number | null): SelectTreeOption<number>[] | undefined => {
    const children = childrenMap.get(parentId)
    if (!children || children.length === 0) return undefined

    return children.map((folder) => {
      const childNodes = buildNodes(folder.id)
      return {
        ...(isBlocked(folder) ? { disabled: true } : {}),
        ...(isCurrentParent(folder) ? { hideMoveButton: true } : {}),
        ...(childNodes ? { children: childNodes } : {}),
        icon: FolderIcon,
        label: `${folder.name}${buildSuffix(folder)}`,
        value: folder.id,
      }
    })
  }

  const topLevelFolders = buildNodes(null)

  const isRootCurrentLocation = editingFolder && currentParentId === null

  const rootOption: SelectTreeOption<number> = {
    ...(topLevelFolders ? { children: topLevelFolders } : {}),
    ...(isRootCurrentLocation ? { hideMoveButton: true } : {}),
    defaultExpanded: true,
    icon: FolderIcon,
    label: t('community-essentials:title'),
    value: 0,
  }

  return [rootOption]
}

export const getEssentialHref = (type: EssentialType, value: string): string | undefined => {
  switch (type) {
    case 'email':
      return `mailto:${value}`
    case 'number':
    case 'texting':
      return `tel:${value}`
    case 'link':
      return value
    case 'image':
    case 'pdf':
      return getAssetLink({ file: value, path: 'chitown-building' })
    default:
      return undefined
  }
}
